// Self-contained chess sound engine built on the Web Audio API.
// Sounds are synthesized at runtime, so there are no audio assets to host
// or license, and everything works offline. Each effect is a short envelope
// of one or more oscillators plus an optional filtered noise "click" so that
// piece moves have a believable wooden "thock".

const STORAGE_KEY = 'livingchess.soundMuted';

let audioCtx = null;
let muted = readMutedPreference();

function readMutedPreference() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function isMuted() {
  return muted;
}

export function setMuted(value) {
  muted = !!value;
  try {
    localStorage.setItem(STORAGE_KEY, muted ? 'true' : 'false');
  } catch {
    /* localStorage unavailable (private mode); preference stays in-memory */
  }
}

export function toggleMuted() {
  setMuted(!muted);
  return muted;
}

// Lazily create (and resume) the shared AudioContext. Browsers require a user
// gesture before audio can start; unlockAudio() below wires that up, but we
// also attempt a resume here in case the context was suspended.
function getContext() {
  if (typeof window === 'undefined') return null;
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;
  if (!audioCtx) {
    try {
      audioCtx = new AudioContextClass();
    } catch {
      return null;
    }
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
}

// --- Recorded samples (HTMLAudioElement) --------------------------------
// Real audio files served from /public/sounds, played through <audio> elements
// rather than Web Audio. Safari reliably plays HTMLAudioElement once the user
// has interacted with the page, whereas a Web Audio context can "run" (the tab
// shows a speaker icon) yet emit silence if it wasn't unlocked inside a gesture.
// Each event maps to one or more files; multiple files = random variation per
// play. If a file is missing/blocked, the matching play function falls back to
// synthesis.
const SAMPLE_FILES = {
  move: [
    'chess_normal_move1.mp3',
    'chess_normal_move2.mp3',
    'chess_normal_move3.mp3',
    'chess_normal_move4.mp3',
  ],
  capture: ['chess_capture_move1.mp3'],
  check: ['chess_check_move1.mp3'],
  drawOffer: ['draw_offer.mp3'],
  drawDecline: ['draw_decline.mp3'],
  gameStart: ['game_start.mp3'],
  gameEnd: ['game_end.mp3'],
};

const audioPool = {
  move: [], capture: [], check: [],
  drawOffer: [], drawDecline: [], gameStart: [], gameEnd: [],
};
let audioPreloaded = false;

function preloadAudioElements() {
  if (audioPreloaded || typeof window === 'undefined' || typeof Audio === 'undefined') return;
  audioPreloaded = true;
  const base = `${process.env.PUBLIC_URL || ''}/sounds`;
  Object.entries(SAMPLE_FILES).forEach(([key, files]) => {
    audioPool[key] = files.map((file) => {
      const el = new Audio(`${base}/${file}`);
      el.preload = 'auto';
      return el;
    });
  });
}

// Start loading the sample files as soon as this module is imported in a
// browser, so the pool is populated well before the first move — regardless of
// when unlockAudio() runs.
preloadAudioElements();

// Play a random recorded sample for the given event. Returns false if none are
// available, so the caller can fall back to synthesis.
function playSample(key, volume = 1) {
  if (muted) return false;
  const pool = audioPool[key];
  if (!pool || pool.length === 0) return false;
  const template = pool[Math.floor(Math.random() * pool.length)];
  // Clone so overlapping plays (e.g. a quick capture) don't cut each other off.
  const node = template.cloneNode(true);
  node.volume = volume;
  const played = node.play();
  if (played && typeof played.catch === 'function') played.catch(() => {});
  return true;
}

// Attach one-time listeners so the first user interaction unlocks audio,
// satisfying browser autoplay policies before the first move ever lands. Also
// preloads the sample files and "warms" the Web Audio context with a silent
// buffer so synth jingles aren't silenced by Safari.
export function unlockAudio() {
  if (typeof window === 'undefined') return;
  preloadAudioElements();
  const handler = () => {
    const ctx = getContext();
    if (ctx) {
      try {
        const warm = ctx.createBufferSource();
        warm.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
        warm.connect(ctx.destination);
        warm.start(0);
      } catch {
        /* ignore — context warm-up is best-effort */
      }
    }
    preloadAudioElements();
    window.removeEventListener('pointerdown', handler);
    window.removeEventListener('keydown', handler);
  };
  window.addEventListener('pointerdown', handler);
  window.addEventListener('keydown', handler);
}

// A single oscillator note with an exponential decay envelope.
function tone(ctx, { freq, start, duration, type = 'sine', gain = 0.2, glideTo = null }) {
  const osc = ctx.createOscillator();
  const env = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  if (glideTo) {
    osc.frequency.exponentialRampToValueAtTime(glideTo, start + duration);
  }
  env.gain.setValueAtTime(0.0001, start);
  env.gain.exponentialRampToValueAtTime(gain, start + 0.008);
  env.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  osc.connect(env).connect(ctx.destination);
  osc.start(start);
  osc.stop(start + duration + 0.02);
}

// Modal synthesis of a wooden piece striking the board. Real wood impacts are
// a sharp contact transient plus a handful of fast-decaying resonant modes at
// inharmonic frequencies — dry, with almost no sustain. That "thock" is what a
// single glide tone can't capture, so we sum several damped sine modes instead.
function woodImpact(ctx, start, { pitch = 240, gain = 0.5, brightness = 1, decay = 0.12 } = {}) {
  // Inharmonic partial ratios typical of a small struck wooden body.
  const modes = [
    { ratio: 1.0, amp: 1.0, decay: 1.0 },
    { ratio: 2.13, amp: 0.55, decay: 0.7 },
    { ratio: 3.41, amp: 0.32, decay: 0.5 },
    { ratio: 4.92, amp: 0.18, decay: 0.35 },
  ];

  modes.forEach((m) => {
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = pitch * m.ratio;
    const d = decay * m.decay;
    const peak = gain * m.amp * (m.ratio > 1 ? brightness : 1);
    // Near-instant attack, exponential body decay — the hallmark of an impact.
    env.gain.setValueAtTime(0.0001, start);
    env.gain.exponentialRampToValueAtTime(peak, start + 0.002);
    env.gain.exponentialRampToValueAtTime(0.0001, start + d);
    osc.connect(env).connect(ctx.destination);
    osc.start(start);
    osc.stop(start + d + 0.02);
  });

  // Short, bright contact "tick" — the click of piece meeting board.
  const tickDur = 0.012;
  const frames = Math.floor(ctx.sampleRate * tickDur);
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / frames, 2);
  }
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 1800;
  const tickEnv = ctx.createGain();
  tickEnv.gain.value = 0.5 * brightness;
  src.connect(hp).connect(tickEnv).connect(ctx.destination);
  src.start(start);
  src.stop(start + tickDur + 0.01);
}

function withContext(play) {
  if (muted) return;
  const ctx = getContext();
  if (!ctx) return;
  try {
    play(ctx, ctx.currentTime);
  } catch {
    /* never let an audio glitch break gameplay */
  }
}

// --- Individual effects -------------------------------------------------

// Ordinary move — recorded sample (random variation), synth thock as fallback.
export function playMove() {
  if (playSample('move')) return;
  withContext((ctx, t) => {
    woodImpact(ctx, t, { pitch: 250, gain: 0.5, brightness: 0.9, decay: 0.11 });
  });
}

// Capture — recorded sample, with a heavier synth knock as fallback.
export function playCapture() {
  if (playSample('capture')) return;
  withContext((ctx, t) => {
    woodImpact(ctx, t, { pitch: 165, gain: 0.62, brightness: 1.5, decay: 0.16 });
    woodImpact(ctx, t + 0.022, { pitch: 300, gain: 0.28, brightness: 1.1, decay: 0.08 });
  });
}

// Check — recorded sample, with the two-tone synth alert as fallback.
export function playCheck() {
  if (playSample('check')) return;
  withContext((ctx, t) => {
    tone(ctx, { freq: 660, start: t, duration: 0.1, type: 'square', gain: 0.12 });
    tone(ctx, { freq: 880, start: t + 0.09, duration: 0.13, type: 'square', gain: 0.12 });
  });
}

// Double wooden thock for castling — king then rook.
export function playCastle() {
  withContext((ctx, t) => {
    woodImpact(ctx, t, { pitch: 240, gain: 0.45, brightness: 0.9, decay: 0.1 });
    woodImpact(ctx, t + 0.13, { pitch: 210, gain: 0.45, brightness: 0.9, decay: 0.1 });
  });
}

// Rising arpeggio for a pawn promotion.
export function playPromote() {
  withContext((ctx, t) => {
    const notes = [392, 523.25, 659.25, 783.99]; // G4 C5 E5 G5
    notes.forEach((freq, i) => {
      tone(ctx, { freq, start: t + i * 0.08, duration: 0.18, type: 'triangle', gain: 0.14 });
    });
  });
}

// Warm rising chime when the game begins. Recorded sample, synth fallback.
export function playGameStart() {
  if (playSample('gameStart')) return;
  withContext((ctx, t) => {
    const notes = [523.25, 659.25, 783.99]; // C5 E5 G5
    notes.forEach((freq, i) => {
      tone(ctx, { freq, start: t + i * 0.1, duration: 0.3, type: 'sine', gain: 0.16 });
    });
  });
}

// Resolved descending chord when the game ends (checkmate, resign, draw,
// timeout). Recorded sample, synth fallback.
export function playGameEnd() {
  if (playSample('gameEnd')) return;
  withContext((ctx, t) => {
    const notes = [659.25, 523.25, 392, 261.63]; // E5 C5 G4 C4
    notes.forEach((freq, i) => {
      tone(ctx, { freq, start: t + i * 0.14, duration: 0.45, type: 'sine', gain: 0.16 });
    });
  });
}

// Polite descending "doorbell" chime when a draw is offered — distinct from the
// move/check cues so a King notices the request. Recorded sample (so it carries
// to a background tab), synth as fallback.
export function playDrawOffer() {
  if (playSample('drawOffer')) return;
  withContext((ctx, t) => {
    tone(ctx, { freq: 784, start: t, duration: 0.35, type: 'sine', gain: 0.16 });
    tone(ctx, { freq: 523.25, start: t + 0.22, duration: 0.4, type: 'sine', gain: 0.16 });
  });
}

// Short, soft descending blip when a draw offer is declined.
export function playDrawDecline() {
  if (playSample('drawDecline')) return;
  withContext((ctx, t) => {
    tone(ctx, { freq: 440, start: t, duration: 0.12, type: 'sine', gain: 0.12 });
    tone(ctx, { freq: 330, start: t + 0.1, duration: 0.16, type: 'sine', gain: 0.12 });
  });
}

// Map a chess.js SAN string to the right sample, by priority: a move that gives
// check (or checkmate) uses the check sound; otherwise a capture uses the
// capture sound; everything else (incl. castling and promotion) is a normal
// move. The game-end fanfare for checkmate is handled separately by the
// status → 'completed' transition in useSoundEffects.
export function playMoveSound(san) {
  if (!san) {
    playMove();
    return;
  }

  const isCheck = san.includes('+') || san.includes('#');
  const isCapture = san.includes('x');

  if (isCheck) {
    playCheck();
  } else if (isCapture) {
    playCapture();
  } else {
    playMove();
  }
}
